import { useState } from 'react';

import {
  Alert,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import uniq from 'lodash/uniq';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from 'react-query';
import Subtitle from 'src/components/Subtitle';
import { notify } from 'src/pages/Calculator/utils/handleNotify';
import { getFundClassAndSeries, getFundsHistory } from 'src/services/operation';

const PERIOD_OPTIONS = [
  {
    label: '12 Meses',
    value: 12,
  },
  {
    label: '24 Meses',
    value: 24,
  },
  {
    label: 'Tudo',
    value: null,
  },
];

export default function OperationFIDCLiabilityRentabilityHistory({
  cnpj,
  name,
}) {
  const theme = useTheme();

  const [chartSeries, setChartSeries] = useState([]);
  const [chartXAxis, setChartXAxis] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(12);
  const [classSerieSelected, setClassSerieSelected] = useState('');
  const [classAndSeriesOptions, setClassAndSeriesOptions] = useState([]);

  const { isLoading: isClassAndSeriesLoading } = useQuery(
    ['fund-classes', cnpj],
    () => getFundClassAndSeries({ cnpj, name }),
    {
      onSuccess: (response) => {
        const classAndSeries = uniq(
          (response?.data ?? [])
            .map(({ classSerie, value, label }) => classSerie ?? value ?? label)
            .filter(Boolean),
        );

        if (!classAndSeries.length) {
          setClassAndSeriesOptions([]);
          setClassSerieSelected('');
          setChartSeries([]);
          setChartXAxis([]);
          return;
        }

        const initialClassSerie = classAndSeries[0];

        setClassAndSeriesOptions(classAndSeries);
        setClassSerieSelected(initialClassSerie);
      },
    },
  );

  const { isLoading: isFundHistoriesLoading } = useQuery(
    ['fund-histories', cnpj, selectedPeriod, classSerieSelected],
    () =>
      getFundsHistory({
        CNPJs: [cnpj],
        period: selectedPeriod,
        classSerie: classSerieSelected,
      }),
    {
      onSuccess: (histories) => {
        if (!histories?.length) {
          setChartSeries([]);
          setChartXAxis([]);
          return;
        }

        const formattedHistories = histories
          .map(({ dataCompetencia, rentabilidadeMensalPct }) => ({
            dataCompetencia: new Date(dataCompetencia),
            rentabilidadeMensalPct,
          }))
          .sort((a, b) => a.dataCompetencia - b.dataCompetencia);

        const allDates = formattedHistories.map(({ dataCompetencia }) =>
          format(dataCompetencia, 'MM/yyyy', { locale: ptBR }),
        );

        const rentabilityHistory = formattedHistories.map(
          ({ rentabilidadeMensalPct }) => rentabilidadeMensalPct,
        );

        setChartSeries([
          {
            name,
            data: rentabilityHistory,
            color: theme.palette.primary.main,
          },
        ]);
        setChartXAxis(allDates);
      },
      onError: () => notify('Erro ao buscar histórico do fundo A', 'error'),
      enabled: Boolean(classSerieSelected),
    },
  );

  const hasClassAndSeriesOptions = classAndSeriesOptions.length > 0;
  const isLoading = isClassAndSeriesLoading || isFundHistoriesLoading;

  const handleClickPeriod = (period) => {
    setSelectedPeriod(period);
  };

  const handleChangeClassSerie = (event) => {
    const { value } = event.target;
    setClassSerieSelected(value);
  };

  return (
    <div>
      <Subtitle title="Histórico de rentabilidade" />
      <Stack my={2} flexDirection="row" justifyContent="space-between" gap={1}>
        {hasClassAndSeriesOptions && (
          <Select
            placeholder="Classe"
            value={classSerieSelected}
            onChange={handleChangeClassSerie}>
            {classAndSeriesOptions.map((classAndSeriesOption) => (
              <MenuItem key={classAndSeriesOption} value={classAndSeriesOption}>
                {classAndSeriesOption
                  .replace(/\?nica/g, 'Única')
                  .replace(/\?nico/g, 'Único')
                  .replace(/S\?rie/g, 'Série')}
              </MenuItem>
            ))}
          </Select>
        )}
        <Stack my={2} flexDirection="row" gap={1}>
          {!isLoading &&
            hasClassAndSeriesOptions &&
            PERIOD_OPTIONS.map((option) => (
              <Typography
                key={option.value}
                sx={{
                  cursor: 'pointer',
                  textDecoration:
                    option.value === selectedPeriod ? 'underline' : 'none',
                }}
                variant="caption"
                onClick={() => handleClickPeriod(option.value)}>
                {option.label}
              </Typography>
            ))}
        </Stack>
      </Stack>
      <Stack mb={2}>
        <Alert severity="info">
          Os dados de rentabilidade das cotas de FIDC apresentados são
          provenientes dos informes mensais disponibilizados à Comissão de
          Valores Mobiliários (CVM). A Uqbar não se responsabiliza por eventuais
          divergências ou inconsistências nas informações fornecidas pelos
          administradores dos fundos.
        </Alert>
      </Stack>
      <ReactApexChart
        series={chartSeries}
        options={{
          chart: {
            height: 350,
            type: 'line',
            zoom: {
              enabled: true,
            },
          },
          dataLabels: {
            enabled: false,
          },
          stroke: {
            curve: 'straight',
          },
          title: {
            text: '',
            align: 'left',
          },
          grid: {
            row: {
              colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
              opacity: 0.5,
            },
          },
          xaxis: {
            categories: chartXAxis,
          },
          noData: {
            text: 'Sem dados de rentabilidade disponíveis.',
            style: {
              color: theme.palette.text.disabled,
              fontSize: '14px',
              fontFamily: theme.typography.fontFamily,
            },
            offsetY: -20,
          },
        }}
      />
    </div>
  );
}
